import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, ArrowRight, Loader2, Shuffle } from 'lucide-react';
import { API } from '../App';

const MODE_CONFIG = {
  panel: { title: 'Try a Quick Fray', subtitle: 'Two AI voices debate your scenario — no signup needed.', accent: '#FF4D00', buttonText: 'Run Free Fray', placeholder: 'e.g., Should we switch from React to Next.js for our SaaS product?' },
  cat_fight: { title: 'Quick Cat Fight', subtitle: 'No-holds-barred AI argument. No signup needed.', accent: '#EF4444', buttonText: 'START FIGHT', placeholder: 'e.g., Is pineapple on pizza a legitimate topping?' },
  trip: { title: 'Quick Trip', subtitle: 'AI under the influence debates your topic. No signup needed.', accent: '#A855F7', buttonText: 'Start Trip', placeholder: 'e.g., What is consciousness? Does free will exist?' },
};

const SUBSTANCES = [
  { slug: 'mushrooms', emoji: '🍄', name: 'Mushrooms' },
  { slug: 'weed', emoji: '🌿', name: 'Weed' },
  { slug: 'drunk', emoji: '🍺', name: 'Drunk' },
  { slug: 'wired', emoji: '⚡', name: 'Wired' },
  { slug: 'acid', emoji: '🌈', name: 'Acid' },
  { slug: 'microdose', emoji: '💊', name: 'Microdose' },
];

export default function TryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'panel';
  const config = MODE_CONFIG[mode] || MODE_CONFIG.panel;

  const [scenario, setScenario] = useState('');
  const [substance, setSubstance] = useState('mushrooms');
  const [launching, setLaunching] = useState(false);
  const [frayId, setFrayId] = useState(null);
  const [events, setEvents] = useState([]);
  const [verdict, setVerdict] = useState(null);
  const [error, setError] = useState('');
  const [topicLoading, setTopicLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const loadRandomTopic = async () => {
    setTopicLoading(true);
    try {
      const res = await API.get('/api/cat-fight/random-topic');
      setScenario(res.data.topic || '');
    } catch {}
    setTopicLoading(false);
  };

  const handleTry = async () => {
    if (!scenario.trim() || scenario.length < 10) { setError('Describe your scenario (at least 10 characters)'); return; }
    setLaunching(true);
    setError('');
    try {
      const body = { scenario, mode };
      if (mode === 'trip') body.substance = substance;
      const res = await API.post('/api/try', body);
      const id = res.data.id || res.data.fray_id;
      setFrayId(id);
      // Stream events
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/frays/${id}/stream?after_seq=0`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        let eventType = '', eventData = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) eventType = line.slice(7).trim();
          else if (line.startsWith('data: ')) eventData = line.slice(6).trim();
          else if (line === '' && eventType && eventData) {
            try {
              const data = JSON.parse(eventData);
              if (eventType === 'voice_message' && data.status === 'streaming') {
                setEvents(prev => [...prev, { voice: data.voice, text: data.text, color: data.voice_color || '#A0A0A0' }]);
              } else if (eventType === 'verdict') {
                setVerdict(data);
              }
            } catch {}
            eventType = ''; eventData = '';
          }
        }
      }
    } catch (err) {
      setError('Failed to start — try again in a moment');
    }
    setLaunching(false);
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <div className="w-7 h-7 bg-[#FF4D00] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}>
            <Zap className="w-4 h-4 text-black" />
          </div>
          <span className="text-lg font-semibold font-['Barlow_Condensed']">Fray</span>
        </Link>

        {/* Mode tabs */}
        <div className="flex gap-2 mb-8">
          {Object.entries(MODE_CONFIG).map(([key, cfg]) => (
            <Link key={key} to={`/try?mode=${key}`}
              className={`px-3 py-2 min-h-[44px] text-xs font-mono border transition-colors ${mode === key ? `border-current bg-opacity-10` : 'border-[#2A2A2A] text-[#A0A0A0]'}`}
              style={mode === key ? { color: cfg.accent, borderColor: cfg.accent, backgroundColor: cfg.accent + '15' } : {}}>
              {key === 'panel' ? 'Quick Fray' : key === 'cat_fight' ? 'Cat Fight' : 'Trip'}
            </Link>
          ))}
        </div>

        <h1 className="text-2xl md:text-4xl font-bold font-['Barlow_Condensed'] mb-3" style={{ color: config.accent }} data-testid="try-title">{config.title}</h1>
        <p className="text-[#A0A0A0] text-base md:text-lg mb-8">{config.subtitle}</p>

        {!frayId && (
          <div>
            {/* Trip substance picker */}
            {mode === 'trip' && (
              <div className="mb-4">
                <span className="text-xs font-mono text-[#A0A0A0] block mb-2">SUBSTANCE</span>
                <div className="flex flex-wrap gap-2">
                  {SUBSTANCES.map(s => (
                    <button key={s.slug} onClick={() => setSubstance(s.slug)}
                      className={`px-3 py-2 min-h-[44px] text-sm border transition-colors ${substance === s.slug ? 'border-[#A855F7] bg-[#A855F7]/10 text-[#A855F7]' : 'border-[#2A2A2A] text-[#A0A0A0]'}`}>
                      {s.emoji} {s.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Cat fight random topic */}
            {mode === 'cat_fight' && (
              <button onClick={loadRandomTopic} disabled={topicLoading}
                className="mb-4 px-4 py-2 min-h-[44px] bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs font-mono flex items-center gap-2 disabled:opacity-50">
                <Shuffle className="w-3 h-3" /> {topicLoading ? '...' : 'Random Topic'}
              </button>
            )}

            <textarea value={scenario} onChange={e => setScenario(e.target.value)}
              className="w-full h-32 p-4 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F5F5] text-sm resize-none focus:outline-none transition-colors leading-relaxed"
              style={{ borderColor: error ? '#EF4444' : undefined }}
              placeholder={config.placeholder} data-testid="try-input" />
            {error && <p className="text-[#EF4444] text-sm mt-2">{error}</p>}
            <button onClick={handleTry} disabled={launching}
              className="mt-4 w-full sm:w-auto px-8 py-3 min-h-[44px] text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: config.accent }}
              data-testid="try-launch">
              {launching ? <><Loader2 className="w-4 h-4 animate-spin" /> Running...</> : <><Zap className="w-4 h-4" /> {config.buttonText}</>}
            </button>
          </div>
        )}

        {/* Live results */}
        {events.length > 0 && (
          <div className="mt-8 space-y-3" data-testid="try-results">
            {events.map((e, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="p-3 md:p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                <span className="text-xs font-medium font-['Barlow_Condensed'] mb-1 block" style={{ color: e.color }}>{e.voice}</span>
                <p className="text-sm text-[#F5F5F5] leading-relaxed">{e.text}</p>
              </motion.div>
            ))}
            <div ref={endRef} />
          </div>
        )}

        {/* Verdict + CTA */}
        {verdict && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="mt-8 p-4 md:p-6 bg-[#1A1A1A] border-2 text-center"
            style={{ borderColor: verdict.verdict === 'Pass' ? '#22C55E' : verdict.verdict === 'Fail' ? '#EF4444' : '#F59E0B' }}>
            <span className="text-xs font-mono text-[#A0A0A0] block mb-1">VERDICT</span>
            <span className="text-2xl md:text-3xl font-bold font-['Barlow_Condensed']"
              style={{ color: verdict.verdict === 'Pass' ? '#22C55E' : verdict.verdict === 'Fail' ? '#EF4444' : '#F59E0B' }}>
              {verdict.verdict}
            </span>
            <p className="text-sm text-[#A0A0A0] mt-3 max-w-md mx-auto">{verdict.executive_summary}</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[44px] bg-[#FF4D00] text-black font-medium text-sm">
                Create Free Account <ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={() => { setFrayId(null); setEvents([]); setVerdict(null); setScenario(''); }}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[44px] border border-[#2A2A2A] text-sm text-[#A0A0A0]">
                Try Another
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
